// Recharts-backed waterfall for /financials.
// Items: [{ label, value, type: 'baseline' | 'delta' | 'forecast' }]
// Renders cumulative bars showing planned → obligated → actual → forecast deltas.

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from 'recharts';
import './charts.css';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

function buildSeries(items) {
  let running = 0;
  return items.map((it, idx) => {
    const isPivot = it.type === 'baseline' || it.type === 'forecast' || idx === 0;
    const start = isPivot ? 0 : running;
    const value = it.value;
    const end = isPivot ? value : running + value;
    const point = {
      id: it.id || it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      label: it.label,
      type: it.type,
      base: Math.min(start, end),
      delta: Math.abs(end - start),
      end,
      raw: value,
      isPivot,
      richTooltip: it.richTooltip || null,
    };
    running = end;
    return point;
  });
}

export function FinancialWaterfall({
  items = [],
  height = 280,
  unit = '$ M',
  ariaLabel = 'Financial waterfall chart',
}) {
  const tooltip = useOptionalRichTooltipController();
  const data = buildSeries(items);
  return (
    <div className="waterfall" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 20, right: 16, bottom: 24, left: 0 }}>
          <CartesianGrid stroke="#e1e4ea" vertical={false} />
          <XAxis
            dataKey="label"
            stroke="#5a5e66"
            tickLine={false}
            axisLine={{ stroke: '#c4c7cf' }}
            tick={{ fontSize: 11, fontWeight: 600 }}
          />
          <YAxis
            stroke="#5a5e66"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => `${v}`}
            width={50}
          />
          <ReferenceLine y={0} stroke="#1a1c1e" strokeWidth={1} />
          {/* invisible spacer to lift the visible delta bar */}
          <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
          <Bar
            dataKey="delta"
            stackId="wf"
            radius={[4, 4, 4, 4]}
            maxBarSize={48}
            shape={(props) => {
              const entry = props.payload;
              const triggerProps =
                entry?.richTooltip && tooltip
                  ? tooltip.getTriggerProps({
                      surfaceId: entry.richTooltip.surfaceId,
                      payload: entry.richTooltip,
                      testIdPrefix: 'rich-tooltip',
                      ariaLabel: `${entry.label} ${entry.raw} ${unit}`,
                    })
                  : {};
              return (
                <rect
                  {...triggerProps}
                  className="rich-tooltip-svg-trigger waterfall-step-trigger"
                  data-testid={`financial-waterfall-step-${entry.id}`}
                  x={props.x}
                  y={props.y}
                  width={props.width}
                  height={props.height}
                  rx={4}
                  ry={4}
                  fill={props.fill}
                  tabIndex={entry?.richTooltip ? 0 : undefined}
                  role={entry?.richTooltip ? 'button' : undefined}
                />
              );
            }}
          >
            {data.map((entry, i) => {
              const color =
                entry.type === 'baseline'
                  ? '#1961a1'
                  : entry.type === 'forecast'
                    ? '#00152c'
                    : entry.raw >= 0
                      ? '#5a8c66'
                      : '#c0524d';
              return <Cell key={i} fill={color} />;
            })}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
